import { Controller } from "@hotwired/stimulus";

// this is used for web-push gem
export default class extends Controller {
  static values = { vapidKey: String };
  static targets = ["statusBadge", "subtitle"];

  connect() {
    // 1. Check UI state immediately on page load
    this.updateUI();

    // 2. If they already granted permission in the past, silently grab their subscription
    if (Notification.permission === "granted") {
      this.registerAndGetToken();
    }

    // 3. Listen for native foreground notifications
    navigator.serviceWorker.addEventListener("message", (event) => {
      console.log("Foreground notification received: ", event.data);
      // Optional: You can trigger a custom Toast UI or alert here
    });
  }

  async enableNotifications(event) {
    event.preventDefault();

    if (Notification.permission === "granted") {
      this.updateUI();
      await this.registerAndGetToken();
      this.hideBannerAndRefresh();
      return;
    }

    const permission = await Notification.requestPermission();
    this.updateUI();

    if (permission === "granted") {
      await this.registerAndGetToken();
    }

    this.hideBannerAndRefresh();
  }

  hideBannerAndRefresh() {
    const banner = document.getElementById("push-prompt-banner");

    if (banner && !banner.classList.contains("hidden")) {
      banner.classList.add("hidden");
      window.location.reload();
    }
  }

  // Native Web Push replaces the old Firebase SDK token logic
  async registerAndGetToken() {
    try {
      // 🛑 CRITICAL FIX: Pointing to your master service worker now!
      const registration =
        await navigator.serviceWorker.register("/service-worker.js");
      await navigator.serviceWorker.ready;

      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        const convertedVapidKey = this.urlBase64ToUint8Array(
          this.vapidKeyValue,
        );

        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedVapidKey,
        });
      }

      if (subscription) {
        await this.saveTokenToBackend(subscription);
      }
    } catch (error) {
      console.error(
        "An error occurred while retrieving native push subscription: ",
        error,
      );
    }
  }

  // Sends the new cryptographic keys to the Rails controller
  async saveTokenToBackend(subscription) {
    const p256dh = btoa(
      String.fromCharCode.apply(
        null,
        new Uint8Array(subscription.getKey("p256dh")),
      ),
    );
    const auth = btoa(
      String.fromCharCode.apply(
        null,
        new Uint8Array(subscription.getKey("auth")),
      ),
    );
    const endpoint = subscription.endpoint;

    if (sessionStorage.getItem("webpush_endpoint_saved") === endpoint) return;

    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      .getAttribute("content");

    const response = await fetch("/push_subscriptions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      // Note: We format this to match what the Rails strong parameters expect
      body: JSON.stringify({
        push_subscription: { endpoint: endpoint, p256dh: p256dh, auth: auth },
      }),
    });

    if (response.ok) {
      sessionStorage.setItem("webpush_endpoint_saved", endpoint);
      console.log(
        "✅ Native Web Push keys successfully synced to your server.",
      );
    }
  }

  updateUI() {
    if (!this.hasStatusBadgeTarget) return;

    if (Notification.permission === "granted") {
      this.statusBadgeTarget.innerText = "Enabled";
      this.statusBadgeTarget.classList.replace(
        "bg-[#0A7C6E]",
        "bg-[#0A7C6E]/20",
      );
      this.statusBadgeTarget.classList.replace("text-white", "text-[#0A7C6E]");

      if (this.hasSubtitleTarget) {
        this.subtitleTarget.innerText = "Alerts are active for this device.";
      }
    } else if (Notification.permission === "denied") {
      this.statusBadgeTarget.innerText = "Blocked";
      this.statusBadgeTarget.classList.replace("bg-[#0A7C6E]", "bg-red-500");

      if (this.hasSubtitleTarget) {
        this.subtitleTarget.innerText = "Unblock in your browser settings.";
      }
    }
  }

  // Helper method to format the VAPID key for the browser's Push API
  urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, "+")
      .replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}
